import { Typography } from '@repo/ui';
import { urls } from '@repo/app-constants';
import { ButtonColors, ButtonSizes, Card, LinkButton } from 'components';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useSubscriptions from 'services/general/subscriptionsList';

import antibioticsSvg from 'assets/images/illustrations/what-causes-disruptions/antibiotics.svg';
import illnessSvg from 'assets/images/illustrations/what-causes-disruptions/illness.svg';
import dietarySvg from 'assets/images/illustrations/what-causes-disruptions/dietary.svg';
import stressSvg from 'assets/images/illustrations/what-causes-disruptions/stress.svg';
import styles from './WhatCausesDisruptionsCard.module.css';

type DisruptionType = {
  illustration: string;
  title: React.ReactNode;
  titleLabel: string;
  description: React.ReactNode;
};

const DISRUPTION_TYPES: DisruptionType[] = [
  {
    illustration: antibioticsSvg,
    titleLabel: 'Antibiotics',
    title: 'Antibiotics',
    description:
      'Some can kill off both beneficial and disruptive microbes indiscriminately.',
  },
  {
    illustration: illnessSvg,
    titleLabel: 'Illness / bug',
    title: 'Illness / bug',
    description: "Reshapes who's thriving and who's struggling in your gut.",
  },
  {
    illustration: dietarySvg,
    titleLabel: 'Dietary changes',
    title: 'Dietary changes',
    description: 'Changes which microbes get "fed" in a single week.',
  },
  {
    illustration: stressSvg,
    titleLabel: 'Stress / poor sleep',
    title: 'Stress / poor sleep',
    description:
      'Affects the gut-brain axis and can alter microbial composition.',
  },
];

const orderKitUrlForSubscription = (
  isAnyTinyPlus: boolean,
  isTinyPlus: boolean,
) => {
  if (!isAnyTinyPlus) return urls.CHECKOUT_WITH_KIT_NON_TINY_PLUS;
  return isTinyPlus
    ? urls.CHECKOUT_WITH_KIT_TINY_PLUS
    : urls.CHECKOUT_WITH_KIT_TINY_PLUS_PROGRAM_OR_PROACTIVE;
};

const WhatCausesDisruptionsCard = () => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs(currentKit);
  const { isAnyTinyPlus, isTinyPlus } = useSubscriptions();
  const { trackEvent } = useAnalytics();

  const showRetestCallout =
    !isPortalConfigLoading && !portalConfig.hide_upsells;

  const cartUrl = `${orderKitUrlForSubscription(isAnyTinyPlus, isTinyPlus)}&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=grs-retest`;

  return (
    <Card className={styles.card} variant='wrapper'>
      <div className={styles.cardInner}>
        <div className={styles.header}>
          <Typography variant='heading-m' className={styles.title}>
            What causes disruptions
          </Typography>
          <Typography variant='body-s' className={styles.intro}>
            A gut disruption is anything that throws off your microbiome's
            balance — causing some species to drop off, unfriendly ones to rise,
            and your gut to shift in ways you can feel.
          </Typography>
        </div>

        <ul className={styles.types}>
          {DISRUPTION_TYPES.map(
            ({ illustration, title, titleLabel, description }) => (
              <li key={titleLabel} className={styles.type}>
                <div className={styles.typeIcon}>
                  <img
                    src={illustration}
                    alt=''
                    aria-hidden='true'
                    className={styles.illustration}
                  />
                </div>
                <div className={styles.typeText}>
                  <Typography variant='subheading' className={styles.typeTitle}>
                    {title}
                  </Typography>
                  <Typography
                    variant='caption'
                    className={styles.typeDescription}
                  >
                    {description}
                  </Typography>
                </div>
              </li>
            ),
          )}
        </ul>

        {showRetestCallout && (
          <div className={styles.retestCallout}>
            <Typography variant='heading-s'>When to retest</Typography>

            <Typography variant='caption' className={styles.retestDescription}>
              If you've recently experienced a gut disruption, we recommend
              retesting in 3 months to see how well your gut is recovering.
            </Typography>
            <LinkButton
              href={cartUrl}
              external
              size={ButtonSizes.S}
              color={ButtonColors.PURPLE}
              variant='outline'
              label='Order your next kit'
              className={styles.retestButton}
              onClick={() => trackEvent('GRS Retest CTA Clicked', {})}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default WhatCausesDisruptionsCard;
